import { Box, rgba, Slider, Tooltip, useMantineTheme } from "@mantine/core";
import { useCallback, useEffect, useRef } from "react";
import classes from "./RestrictedRangeSlider.module.css";

interface RestrictedRangeSliderProps {
  subjectColorIndex: string;
  value: number;
  onChange: (value: number) => void;
  minValid: number;
  maxValid: number;
  maxValue: number; // The maximum value for the slider (limitValue)
  disabled: boolean;
}

export default function RestrictedRangeSlider({
  disabled,
  subjectColorIndex,
  value,
  onChange,
  minValid,
  maxValid,
  maxValue,
}: RestrictedRangeSliderProps) {
  const theme = useMantineTheme();
  const sliderRef = useRef<HTMLDivElement>(null);

  const handleChange = useCallback(
    (newValue: number) => {
      const clampedValue = Math.max(minValid, Math.min(maxValid, newValue));
      if (clampedValue !== value) {
        onChange(clampedValue);
      }
    },
    [minValid, maxValid, value, onChange]
  );

  useEffect(() => {
    const sliderElement = sliderRef.current;
    if (!sliderElement) return;

    const handleTrackClick = (e: Event) => {
      const mouseEvent = e as MouseEvent;
      const track = sliderElement.querySelector(
        ".mantine-Slider-track"
      ) as HTMLElement;
      if (!track) return;

      const rect = track.getBoundingClientRect();
      const clickX = mouseEvent.clientX - rect.left;
      const percentage = (clickX / rect.width) * 100;
      const absoluteValue = (percentage / 100) * maxValue;

      if (absoluteValue < minValid) {
        handleChange(minValid);
        mouseEvent.preventDefault();
        mouseEvent.stopPropagation();
      } else if (absoluteValue > maxValid) {
        handleChange(maxValid);
        mouseEvent.preventDefault();
        mouseEvent.stopPropagation();
      }
    };

    const track = sliderElement.querySelector(".mantine-Slider-track");
    if (track) {
      track.addEventListener("click", handleTrackClick);
      return () => {
        track.removeEventListener("click", handleTrackClick);
      };
    }
  }, [minValid, maxValid, maxValue, handleChange]);

  const minValidPercent = maxValue > 0 ? (minValid / maxValue) * 100 : 0;
  const maxValidPercent = maxValue > 0 ? (maxValid / maxValue) * 100 : 100;

  const tooltipLabel = "Add more content or reduce test limit to enable";

  if (disabled) {
    return (
      <Box ref={sliderRef} pos="relative" className={classes.wrapper}>
        <Slider
          disabled={disabled}
          color={subjectColorIndex}
          value={value}
          onChange={handleChange}
          min={0}
          max={maxValue}
          label={null}
          marks={getBarMarks(maxValue)}
        />
      </Box>
    );
  }

  return (
    <Box ref={sliderRef} pos="relative" className={classes.wrapper}>
      <Slider
        color={subjectColorIndex}
        value={value}
        onChange={handleChange}
        min={0}
        max={maxValue}
        label={null}
        marks={getBarMarks(maxValue)}
        styles={{
          track: {
            position: "relative",
          },
          bar: {
            left: `${minValidPercent}%`,
            width: `${maxValidPercent - minValidPercent}%`,
          },
          thumb: {
            zIndex: 3,
          },
        }}
      />
      {/* Left disabled overlay (0 to minValid) */}
      {minValid > 0 && (
        <DisabledOverlayWithTooltip
          tooltipLabel={tooltipLabel}
          className={classes.disabledOverlay}
          style={{
            left: 0,
            width: `${minValidPercent}%`,
            backgroundColor: rgba(theme.colors.incorrect[5], 0.6),
            borderColor: theme.colors.incorrect[5],
          }}
        />
      )}
      {/* Right disabled overlay (maxValid to maxValue) */}
      {maxValid < maxValue && (
        <DisabledOverlayWithTooltip
          tooltipLabel={tooltipLabel}
          className={classes.disabledOverlay}
          style={{
            left: `${maxValidPercent}%`,
            width: `${100 - maxValidPercent}%`,
            backgroundColor: rgba(theme.colors.incorrect[5], 0.6),
            borderColor: theme.colors.incorrect[5],
          }}
        />
      )}
    </Box>
  );
}

interface DisabledOverlayWithTooltipProps {
  tooltipLabel: string;
  className: string;
  style: React.CSSProperties;
}

function DisabledOverlayWithTooltip({
  tooltipLabel,
  className,
  style,
}: DisabledOverlayWithTooltipProps) {
  return (
    <Tooltip label={tooltipLabel} withArrow position="top" color="incorrect">
      <Box className={className} style={style} />
    </Tooltip>
  );
}

function getBarMarks(maxValue: number) {
  if (maxValue <= 0) {
    return [];
  }

  let interval: number;
  if (maxValue < 10) {
    interval = 2;
  } else if (maxValue <= 20) {
    interval = 4;
  } else if (maxValue <= 40) {
    interval = 10;
  } else if (maxValue <= 80) {
    interval = 20;
  } else {
    interval = 40;
  }

  const marks: Array<{ value: number; label: string }> = [];

  marks.push({ value: 0, label: "0" });

  for (let value = interval; value <= maxValue; value += interval) {
    marks.push({ value, label: `${value}` });
  }

  if (maxValue % interval !== 0) {
    marks.push({ value: maxValue, label: `${maxValue}` });
  }

  return marks;
}
